/**
 * gateConfig.ts — access gate for the hosted preview build.
 *
 * Split from SiteGate.tsx because that file may export only components
 * (react-refresh/only-export-components), and because a `siteGate.ts` next to
 * `SiteGate.tsx` collides on case-insensitive filesystems.
 *
 * WHAT THIS IS: a speed bump so the Firebase Hosting preview at
 * itow-db.web.app is not wide open to anyone who happens on the URL.
 *
 * WHAT THIS IS NOT: security. Firebase Hosting serves static files with no
 * server-side auth, so the expected credentials are compiled into the JS
 * bundle and are readable in devtools by anyone who cares to look. The bundle
 * is downloaded *before* this gate renders. Treat everything behind it as
 * public. It exists to keep the preview off casual visitors' screens, not to
 * protect data — which is why it is only ever pointed at seeded test data.
 *
 * Disabled entirely when VITE_SITE_GATE_PASSWORD is unset, so `pnpm dev` and
 * any build without that variable are never gated.
 */

/** localStorage key holding the pass marker. Cleared by clearing site data. */
export const SITE_GATE_STORAGE_KEY = 'itow-site-gate';

const EXPECTED_USER = import.meta.env.VITE_SITE_GATE_USER ?? '';
const EXPECTED_PASSWORD = import.meta.env.VITE_SITE_GATE_PASSWORD ?? '';

/** The gate only engages when a password was supplied at build time. */
export const isSiteGateEnabled = EXPECTED_PASSWORD !== '';

/**
 * Username match is case-insensitive and trimmed — testers type it on phones
 * where autocapitalise is on by default. The password is compared exactly.
 */
export function verifySiteGate(user: string, password: string): boolean {
  return (
    user.trim().toLowerCase() === EXPECTED_USER.trim().toLowerCase() &&
    password === EXPECTED_PASSWORD
  );
}

export function hasPassedSiteGate(): boolean {
  if (!isSiteGateEnabled) {
    return true;
  }
  try {
    return localStorage.getItem(SITE_GATE_STORAGE_KEY) === 'granted';
  } catch {
    // Storage blocked (private mode, embedded webview): re-prompt every load.
    return false;
  }
}

export function recordSiteGatePass(): void {
  try {
    localStorage.setItem(SITE_GATE_STORAGE_KEY, 'granted');
  } catch {
    // Storage blocked — the gate simply re-prompts on the next load.
  }
}
